import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { user, token, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      toast.error('Acces interzis! Doar administratorii pot accesa această pagină.');
      navigate('/');
    } else if (user && user.is_admin) {
      loadData();
    }
  }, [user, authLoading]);

  const loadData = async () => {
    try {
      const [productsRes, postsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/blog`)
      ]);
      setProducts(productsRes.data);
      setBlogPosts(postsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Eroare la încărcarea datelor');
    }
  };

  const handleDeleteProduct = async (slug) => {
    if (!window.confirm('Sigur vrei să ștergi acest produs?')) return;
    
    try {
      await axios.delete(`${API}/products/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produs șters!');
      loadData();
    } catch (error) {
      toast.error('Eroare la ștergerea produsului');
    }
  };

  const handleDeletePost = async (slug) => {
    if (!window.confirm('Sigur vrei să ștergi acest articol?')) return;
    
    try {
      await axios.delete(`${API}/blog/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Articol șters!');
      loadData();
    } catch (error) {
      toast.error('Eroare la ștergerea articolului');
    }
  };

  const ProductForm = ({ product, onClose }) => {
    const [formData, setFormData] = useState(product || {
      slug: '',
      title: '',
      description: '',
      price: '',
      badge: '',
      external_url: '',
      hero_points: [],
      language: 'ro',
      featured: false,
      image_url: ''
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
        const endpoint = product 
          ? `${API}/products/${product.slug}`
          : `${API}/products`;
        const method = product ? 'put' : 'post';
        
        await axios[method](endpoint, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(product ? 'Produs actualizat!' : 'Produs adăugat!');
        loadData();
        onClose();
      } catch (error) {
        toast.error('Eroare la salvarea produsului');
      } finally {
        setLoading(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Slug (URL)</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
              disabled={!!product}
            />
          </div>
          <div>
            <Label>Titlu</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div>
            <Label>Preț</Label>
            <Input
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
            />
          </div>
          <div>
            <Label>Badge</Label>
            <Input
              value={formData.badge}
              onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
            />
          </div>
          <div>
            <Label>Stan.store URL</Label>
            <Input
              value={formData.external_url}
              onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
              required
            />
          </div>
          <div>
            <Label>Imagine URL</Label>
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label>Descriere</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={3}
          />
        </div>
        <div>
          <Label>Puncte cheie (câte unul pe linie)</Label>
          <Textarea
            value={formData.hero_points?.join('\n') || ''}
            onChange={(e) => setFormData({ ...formData, hero_points: e.target.value.split('\n').filter(p => p.trim()) })}
            rows={4}
          />
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.featured}
            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
            className="h-4 w-4"
          />
          <Label>Featured</Label>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
            {product ? 'Actualizează' : 'Adaugă'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Anulează
          </Button>
        </div>
      </form>
    );
  };

  const BlogForm = ({ post, onClose }) => {
    const [formData, setFormData] = useState(post || {
      slug: '',
      title: '',
      subtitle: '',
      content: '',
      category: 'Mindset',
      language: 'ro',
      read_time: 5,
      featured: false,
      image_url: ''
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
        const endpoint = post 
          ? `${API}/blog/${post.slug}`
          : `${API}/blog`;
        const method = post ? 'put' : 'post';
        
        await axios[method](endpoint, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(post ? 'Articol actualizat!' : 'Articol adăugat!');
        loadData();
        onClose();
      } catch (error) {
        toast.error('Eroare la salvarea articolului');
      } finally {
        setLoading(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Slug (URL)</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
              disabled={!!post}
            />
          </div>
          <div>
            <Label>Categorie</Label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full rounded-md border border-slate-300 p-2"
            >
              <option>Mindset</option>
              <option>Budgeting</option>
              <option>Basics</option>
              <option>Side Hustles</option>
              <option>Investing</option>
              <option>Digital Products</option>
            </select>
          </div>
          <div>
            <Label>Limba</Label>
            <select
              value={formData.language}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              className="w-full rounded-md border border-slate-300 p-2"
            >
              <option value="ro">🇷🇴 Română</option>
              <option value="en">🇬🇧 English</option>
            </select>
          </div>
          <div>
            <Label>Timp citire (min)</Label>
            <Input
              type="number"
              value={formData.read_time}
              onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <Label>Imagine URL</Label>
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label>Titlu</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div>
          <Label>Subtitlu</Label>
          <Input
            value={formData.subtitle}
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
          />
        </div>
        <div>
          <Label>Conținut (Markdown)</Label>
          <Textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            required
            rows={10}
            className="font-mono text-sm"
          />
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.featured}
            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
            className="h-4 w-4"
          />
          <Label>Featured</Label>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
            {post ? 'Actualizează' : 'Adaugă'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Anulează
          </Button>
        </div>
      </form>
    );
  };

  if (authLoading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="page-container min-h-screen py-16 bg-gradient-to-br from-blue-50 via-mint-50 to-white">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="mb-2 text-4xl font-bold text-slate-900" data-testid="admin-title">
            Panou Administrare
          </h1>
          <p className="text-lg text-slate-600">Gestionează produsele și articolele de blog</p>
        </div>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Produse ({products.length})
            </TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Blog ({blogPosts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Produse Digitale</CardTitle>
                  <Button
                    onClick={() => setEditingProduct({})}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Adaugă Produs
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {editingProduct !== null ? (
                  <ProductForm product={editingProduct.slug ? editingProduct : null} onClose={() => setEditingProduct(null)} />
                ) : (
                  <div className="space-y-4">
                    {products.map((product) => (
                      <div key={product.id} className="flex items-center justify-between rounded-lg border p-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{product.title}</h3>
                          <p className="text-sm text-slate-600">{product.price} • {product.badge}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingProduct(product)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.slug)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="blog" className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Articole Blog</CardTitle>
                  <Button
                    onClick={() => setEditingPost({})}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Adaugă Articol
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {editingPost !== null ? (
                  <BlogForm post={editingPost.slug ? editingPost : null} onClose={() => setEditingPost(null)} />
                ) : (
                  <div className="space-y-4">
                    {blogPosts.map((post) => (
                      <div key={post.id} className="flex items-center justify-between rounded-lg border p-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{post.title}</h3>
                          <p className="text-sm text-slate-600">{post.category} • {post.read_time} min • {post.language}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingPost(post)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeletePost(post.slug)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;